import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Link from '@mui/material/Link';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CodeIcon from '@mui/icons-material/Code';
import { deleteField, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { TICKET_STATUSES, PRIORITIES } from '../../../constants/tickets';
import { dashboardTicketPath } from '../../../lib/dashboardRoutes';
import {
  ticketCreatorLabel,
  ticketDelegatorAgentId,
  aiAgentDisplayName,
} from '../../../lib/ticketCommentMeta';
import {
  formatAiSpendUsd,
  readTicketAiSpend,
  resolveEffectiveAiSpendCap,
} from '../../../lib/aiSpendBudget';
import {
  clampMaxRejectionCount,
  readRejectionCount,
  resolveEffectiveMaxRejectionCount,
} from '../../../lib/rejectionCap';
import {
  MAX_LINKED_READ_REPOS,
  formatTargetRepoSlug,
} from '../../../lib/ticketTargetRepo';
import { KANBAN_ROLES } from './ticketHubHelpers';
import TicketCommentsThread from '../TicketCommentsThread';
import TicketAssigneeAutocomplete from '../TicketAssigneeAutocomplete';
import TicketRichTextField from '../../rich-text/TicketRichTextField';
import RichTextDisplay from '../../rich-text/RichTextDisplay';
import { db, functions } from '../../../firebase';

const setTicketHumanStoppedFn = httpsCallable(functions, 'setTicketHumanStopped');
const resetTicketAiSpendFn = httpsCallable(functions, 'resetTicketAiSpend');
const resetTicketRejectionCountFn = httpsCallable(
  functions,
  'resetTicketRejectionCount',
);

/**
 * Portable ticket detail body — form fields, activity thread, assignee controls.
 * Used in side-peek panel, maximized overlay, and the new-ticket dialog.
 */
export default function TicketDetailView({
  ticketId,
  onClose,
  editing,
  ticketFormReadOnly,
  formTitle,
  onFormTitleChange,
  formBody,
  onFormBodyChange,
  formStatus,
  onFormStatusChange,
  formPriority,
  onFormPriorityChange,
  formAppId,
  onFormAppIdChange,
  ticketApps = [],
  forceAppSelection = false,
  formTargetRepoKey = '',
  onFormTargetRepoKeyChange,
  formLinkedReadRepoKeys = [],
  onFormLinkedReadRepoKeysChange,
  gameProjectsWithGithub = [],
  appProjectsWithGithub = [],
  formLabelIds,
  onFormLabelIdsChange,
  formAssignee,
  onFormAssigneeChange,
  formAiSpendOverrideCalls = '',
  onFormAiSpendOverrideCalls,
  formAiSpendOverrideSpend = '',
  onFormAiSpendOverrideSpend,
  formRejectionCapOverride = '',
  onFormRejectionCapOverride,
  saving,
  onSave,
  labels,
  assigneeAutocompleteOptions,
  descriptionEditing,
  onDescriptionEditingChange,
  linkedTaskSummary,
  onOpenLinkedTaskInKanban,
  ticketFormSourceAlertTitle,
  ticketSandboxMetaById,
  onViewCodePatches,
  canModifyTicket,
  companyId,
  uid,
  email,
  role,
  userData,
  userByUid,
  agentById,
  formatDateTime,
  timeZoneLabel,
  /** When true, fills parent height with scrollable body + pinned footer. */
  fillHeight = false,
  /** game_projects.aiSpendCap for the ticket's linked project (optional). */
  projectAiSpendCap = null,
  /** companies/{id}.settings.aiSpendCap workspace default (optional). */
  workspaceAiSpendCap = null,
  /** companies/{id}.settings.maxRejectionCount workspace default (optional). */
  workspaceMaxRejectionCount = null,
}) {
  const navigate = useNavigate();
  const resolvedTicketId = ticketId || editing?.id || null;
  const isCreate = !editing;
  const isAdmin = role === 'company_admin' || role === 'super_admin';
  const [humanStopBusy, setHumanStopBusy] = useState(false);
  const [humanStopError, setHumanStopError] = useState('');
  const [aiSpendBusy, setAiSpendBusy] = useState(false);
  const [aiSpendError, setAiSpendError] = useState('');
  const [overrideCalls, setOverrideCalls] = useState('');
  const [overrideSpend, setOverrideSpend] = useState('');
  const [overrideRejectionMax, setOverrideRejectionMax] = useState('');
  const [rejectionBusy, setRejectionBusy] = useState(false);
  const [rejectionError, setRejectionError] = useState('');
  const [linkedReposExpanded, setLinkedReposExpanded] = useState(
    () => Array.isArray(formLinkedReadRepoKeys) && formLinkedReadRepoKeys.length > 0,
  );

  useEffect(() => {
    if (Array.isArray(formLinkedReadRepoKeys) && formLinkedReadRepoKeys.length > 0) {
      setLinkedReposExpanded(true);
    }
  }, [formLinkedReadRepoKeys]);

  const linkedReadRepoOptions = useMemo(() => {
    const options = [];
    for (const project of gameProjectsWithGithub) {
      const key = `game:${project.id}`;
      if (key === formTargetRepoKey) continue;
      options.push({
        key,
        label: `${project.title || 'Untitled game'} (${project.githubOwner}/${project.githubRepoName})`,
        group: 'Game Studio',
      });
    }
    for (const project of appProjectsWithGithub) {
      const key = `app:${project.id}`;
      if (key === formTargetRepoKey) continue;
      options.push({
        key,
        label: `${project.title || project.name || 'Untitled app'} (${project.githubOwner}/${project.githubRepoName})`,
        group: 'AI Studio',
      });
    }
    return options;
  }, [gameProjectsWithGithub, appProjectsWithGithub, formTargetRepoKey]);

  const selectedLinkedReadRepos = useMemo(() => {
    const keys = Array.isArray(formLinkedReadRepoKeys) ? formLinkedReadRepoKeys : [];
    return linkedReadRepoOptions.filter((o) => keys.includes(o.key));
  }, [formLinkedReadRepoKeys, linkedReadRepoOptions]);

  const aiSpend = useMemo(() => readTicketAiSpend(editing), [editing]);
  const effectiveCap = useMemo(
    () =>
      resolveEffectiveAiSpendCap(
        editing,
        { aiSpendCap: projectAiSpendCap },
        { aiSpendCap: workspaceAiSpendCap },
      ),
    [editing, projectAiSpendCap, workspaceAiSpendCap],
  );
  const usageMeterLabel = `AI usage: ${aiSpend.callCount} / ${effectiveCap.maxCallCount} calls, ${formatAiSpendUsd(aiSpend.spendUsd)} / ${formatAiSpendUsd(effectiveCap.maxSpendUsd)}`;

  const workspaceRejectionSettings = useMemo(
    () =>
      workspaceMaxRejectionCount != null
        ? { maxRejectionCount: workspaceMaxRejectionCount }
        : {},
    [workspaceMaxRejectionCount],
  );
  const effectiveRejection = useMemo(
    () =>
      resolveEffectiveMaxRejectionCount(editing, workspaceRejectionSettings),
    [editing, workspaceRejectionSettings],
  );
  const rejectionCount = readRejectionCount(editing?.rejectionCount);
  const rejectionMeterLabel = `Rejections: ${rejectionCount}/${effectiveRejection.maxCount}`;
  const rejectionSourceLabel =
    effectiveRejection.source === 'ticket_override'
      ? 'ticket override'
      : effectiveRejection.source === 'workspace_default'
        ? 'workspace default'
        : 'global default';

  useEffect(() => {
    const ov = editing?.aiSpendCapOverride;
    setOverrideCalls(
      ov && ov.maxCallCount != null && Number(ov.maxCallCount) > 0
        ? String(ov.maxCallCount)
        : '',
    );
    setOverrideSpend(
      ov && ov.maxSpendUsd != null && Number(ov.maxSpendUsd) > 0
        ? String(ov.maxSpendUsd)
        : '',
    );
    const rejOv = editing?.rejectionCapOverride;
    const clamped = clampMaxRejectionCount(rejOv?.maxCount);
    setOverrideRejectionMax(clamped != null ? String(clamped) : '');
  }, [
    editing?.id,
    editing?.aiSpendCapOverride,
    editing?.rejectionCapOverride,
  ]);

  const handleHumanStopToggle = async () => {
    if (!resolvedTicketId || humanStopBusy) return;
    const stopping = editing?.humanStopped !== true;
    if (stopping) {
      const ok = window.confirm('Stop AI work?');
      if (!ok) return;
    }
    setHumanStopBusy(true);
    setHumanStopError('');
    try {
      await setTicketHumanStoppedFn({
        ticketId: resolvedTicketId,
        stopped: stopping,
      });
    } catch (err) {
      console.error('setTicketHumanStopped:', err);
      setHumanStopError(
        err?.message ||
          err?.details ||
          (typeof err?.code === 'string' ? err.code : '') ||
          'Failed to update AI stop state.',
      );
    } finally {
      setHumanStopBusy(false);
    }
  };

  const handleSaveAiSpendOverride = async () => {
    if (!resolvedTicketId || !companyId || aiSpendBusy) return;
    const callsRaw = overrideCalls.trim();
    const spendRaw = overrideSpend.trim();
    const callsN = callsRaw === '' ? null : Number(callsRaw);
    const spendN = spendRaw === '' ? null : Number(spendRaw);
    if (callsRaw !== '' && (!Number.isFinite(callsN) || callsN <= 0)) {
      setAiSpendError('Max calls must be a positive number (or blank).');
      return;
    }
    if (spendRaw !== '' && (!Number.isFinite(spendN) || spendN <= 0)) {
      setAiSpendError('Max spend must be a positive number (or blank).');
      return;
    }
    setAiSpendBusy(true);
    setAiSpendError('');
    try {
      const patch =
        callsN == null && spendN == null
          ? { aiSpendCapOverride: deleteField() }
          : {
              aiSpendCapOverride: {
                ...(callsN != null ? { maxCallCount: Math.floor(callsN) } : {}),
                ...(spendN != null ? { maxSpendUsd: spendN } : {}),
              },
            };
      await updateDoc(doc(db, 'tickets', resolvedTicketId), {
        ...patch,
        updatedAt: new Date(),
        updatedBy: uid || null,
      });
    } catch (err) {
      console.error('save aiSpendCapOverride:', err);
      setAiSpendError(err?.message || 'Failed to save AI spend override.');
    } finally {
      setAiSpendBusy(false);
    }
  };

  const handleResetAiSpend = async () => {
    if (!resolvedTicketId || aiSpendBusy) return;
    const ok = window.confirm(
      'Reset AI spend counters on this ticket to zero? This does not change the cap.',
    );
    if (!ok) return;
    setAiSpendBusy(true);
    setAiSpendError('');
    try {
      await resetTicketAiSpendFn({ ticketId: resolvedTicketId });
    } catch (err) {
      console.error('resetTicketAiSpend:', err);
      setAiSpendError(
        err?.message ||
          err?.details ||
          (typeof err?.code === 'string' ? err.code : '') ||
          'Failed to reset AI spend.',
      );
    } finally {
      setAiSpendBusy(false);
    }
  };

  const handleSaveRejectionCapOverride = async () => {
    if (!resolvedTicketId || !companyId || rejectionBusy) return;
    const raw = overrideRejectionMax.trim();
    if (raw === '') {
      setRejectionBusy(true);
      setRejectionError('');
      try {
        await updateDoc(doc(db, 'tickets', resolvedTicketId), {
          rejectionCapOverride: deleteField(),
          updatedAt: new Date(),
          updatedBy: uid || null,
        });
      } catch (err) {
        console.error('clear rejectionCapOverride:', err);
        setRejectionError(
          err?.message || 'Failed to clear rejection cap override.',
        );
      } finally {
        setRejectionBusy(false);
      }
      return;
    }
    const clamped = clampMaxRejectionCount(raw);
    if (clamped == null) {
      setRejectionError('Override max rejections must be an integer from 1 to 20 (or blank).');
      return;
    }
    setRejectionBusy(true);
    setRejectionError('');
    setOverrideRejectionMax(String(clamped));
    try {
      await updateDoc(doc(db, 'tickets', resolvedTicketId), {
        rejectionCapOverride: { maxCount: clamped },
        updatedAt: new Date(),
        updatedBy: uid || null,
      });
    } catch (err) {
      console.error('save rejectionCapOverride:', err);
      setRejectionError(
        err?.message || 'Failed to save rejection cap override.',
      );
    } finally {
      setRejectionBusy(false);
    }
  };

  const handleResetRejectionCount = async () => {
    if (!resolvedTicketId || rejectionBusy) return;
    const ok = window.confirm(
      'Reset QA rejection count to zero? If this ticket was suspended for rejection strikeout, it will also be returned to In Progress.',
    );
    if (!ok) return;
    setRejectionBusy(true);
    setRejectionError('');
    try {
      await resetTicketRejectionCountFn({ ticketId: resolvedTicketId });
    } catch (err) {
      console.error('resetTicketRejectionCount:', err);
      setRejectionError(
        err?.message ||
          err?.details ||
          (typeof err?.code === 'string' ? err.code : '') ||
          'Failed to reset rejection count.',
      );
    } finally {
      setRejectionBusy(false);
    }
  };

  const body = (
    <Stack spacing={2} sx={{ mt: fillHeight ? 0 : 1 }}>
      {editing?.linkedTaskId && (
        <Alert
          severity="info"
          icon={false}
          sx={{
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Chip
            color="primary"
            variant="outlined"
            label={`Linked to Task: ${linkedTaskSummary?.title || editing.linkedTaskId}`}
            sx={{ fontWeight: 600 }}
          />
          {KANBAN_ROLES.includes(role) && (
            <Button
              size="small"
              variant="contained"
              startIcon={<ViewColumnIcon />}
              onClick={onOpenLinkedTaskInKanban}
            >
              Open in Kanban
            </Button>
          )}
        </Alert>
      )}
      {!editing && ticketFormSourceAlertTitle && (
        <Alert severity="info" sx={{ py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            From Alert:{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {ticketFormSourceAlertTitle}
            </Box>
          </Typography>
        </Alert>
      )}
      {editing && (
        <Typography variant="caption" color="text.secondary" display="block">
          Created by {ticketCreatorLabel(editing, userByUid, agentById)}
          {formatDateTime(editing.createdAt) !== '—'
            ? ` · ${formatDateTime(editing.createdAt)}`
            : ''}{' '}
          ({timeZoneLabel})
        </Typography>
      )}
      {editing?.source === 'ai_agent_delegation' && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="body2">
            This ticket was created by an AI agent
            {(() => {
              const delegatorId = ticketDelegatorAgentId(editing);
              const delegatorName = delegatorId
                ? aiAgentDisplayName(
                    agentById[delegatorId],
                    delegatorId,
                    editing.delegatedByAgentName || '',
                    agentById,
                  )
                : '';
              return delegatorName ? ` (${delegatorName})` : '';
            })()}
            {editing.parentTicketId ? (
              <>
                {' '}
                from parent ticket{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate(dashboardTicketPath(editing.parentTicketId))}
                >
                  {editing.parentTicketId}
                </Link>
              </>
            ) : null}
            .
          </Typography>
        </Alert>
      )}
      {editing?.id && ticketSandboxMetaById[editing.id]?.total > 0 && (
        <Alert
          severity="info"
          icon={<CodeIcon />}
          sx={{ py: 0.5 }}
          action={
            <Button
              size="small"
              variant="outlined"
              startIcon={<CodeIcon />}
              onClick={() => onViewCodePatches(editing.id)}
            >
              View Code Patches
            </Button>
          }
        >
          <Typography variant="body2">
            {ticketSandboxMetaById[editing.id].pending > 0
              ? `${ticketSandboxMetaById[editing.id].pending} code patch${
                  ticketSandboxMetaById[editing.id].pending === 1 ? '' : 'es'
                } awaiting review.`
              : `${ticketSandboxMetaById[editing.id].total} linked code patch${
                  ticketSandboxMetaById[editing.id].total === 1 ? '' : 'es'
                } (all reviewed).`}
          </Typography>
        </Alert>
      )}
      <TextField
        label="Title"
        value={formTitle}
        onChange={(e) => onFormTitleChange(e.target.value)}
        fullWidth
        required
        disabled={ticketFormReadOnly}
      />
      {resolvedTicketId && companyId && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <TicketCommentsThread
            ticketId={resolvedTicketId}
            companyId={companyId}
            ticketTitle={editing?.title || formTitle}
            canPost={editing ? canModifyTicket(editing) : false}
            actorUid={uid}
            actorEmail={email}
            actorRole={role}
            actorDisplayName={userData?.displayName || ''}
            userData={userData}
            participantTicket={{
              reporterUid: editing?.reporterUid,
              assigneeUid: editing?.isAssignedToAi ? null : editing?.assigneeUid,
              assigneeAgentId: editing?.assigneeAgentId || null,
              isAssignedToAi: editing?.isAssignedToAi === true,
              status: editing?.status || '',
              platformEscalation: editing?.platformEscalation,
            }}
          />
        </>
      )}
      <Box>
        {editing && !descriptionEditing ? (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Description</Typography>
              {canModifyTicket(editing) && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => onDescriptionEditingChange(true)}
                >
                  Edit description
                </Button>
              )}
            </Stack>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'background.default',
                cursor: canModifyTicket(editing) ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (canModifyTicket(editing)) onDescriptionEditingChange(true);
              }}
            >
              {formBody ? (
                <RichTextDisplay body={formBody} bodyFormat={editing.bodyFormat} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No description provided.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                {editing.bodyUpdatedAt
                  ? `Updated ${formatDateTime(editing.bodyUpdatedAt)}`
                  : `Created ${formatDateTime(editing.createdAt)}`}{' '}
                ({timeZoneLabel})
              </Typography>
            </Paper>
          </>
        ) : (
          <TicketRichTextField
            label="Description"
            value={formBody}
            onChange={onFormBodyChange}
            userData={userData}
            ticketId={resolvedTicketId}
            disabled={ticketFormReadOnly}
            minRows={3}
            placeholder="What needs to be done? Include context, steps to reproduce, or acceptance criteria."
          />
        )}
      </Box>
      <FormControl fullWidth size="small" disabled={ticketFormReadOnly}>
        <InputLabel>Status</InputLabel>
        <Select label="Status" value={formStatus} onChange={(e) => onFormStatusChange(e.target.value)}>
          {TICKET_STATUSES.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {editing && resolvedTicketId && (
        <Stack spacing={1}>
          {editing.aiBudgetExceeded === true && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              AI spend budget exceeded. Agents and Judge will not run until an
              admin raises the cap or resets spend.
            </Alert>
          )}
          <Typography
            variant="body2"
            color={
              editing.aiBudgetExceeded === true ? 'warning.main' : 'text.secondary'
            }
            sx={{ fontWeight: editing.aiBudgetExceeded === true ? 600 : 400 }}
          >
            {usageMeterLabel}
            {effectiveCap.source === 'ticket_override'
              ? ' (ticket override)'
              : effectiveCap.source === 'project_default'
                ? ' (project default)'
                : ' (global default)'}
          </Typography>
          <Typography
            variant="body2"
            color={
              editing.status === 'awaiting_human_intervention' ||
              rejectionCount >= effectiveRejection.maxCount
                ? 'warning.main'
                : 'text.secondary'
            }
            sx={{
              fontWeight:
                editing.status === 'awaiting_human_intervention' ||
                rejectionCount >= effectiveRejection.maxCount
                  ? 600
                  : 400,
            }}
          >
            {rejectionMeterLabel} ({rejectionSourceLabel})
          </Typography>
          {isAdmin && (
            <>
              {editing.humanStopped === true && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  AI work on this ticket is stopped. Agents will not run until
                  resumed.
                </Alert>
              )}
              {(humanStopError || aiSpendError || rejectionError) && (
                <Alert
                  severity="error"
                  sx={{ py: 0.5 }}
                  onClose={() => {
                    setHumanStopError('');
                    setAiSpendError('');
                    setRejectionError('');
                  }}
                >
                  {rejectionError || aiSpendError || humanStopError}
                </Alert>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  label="Override max calls"
                  value={overrideCalls}
                  onChange={(e) => setOverrideCalls(e.target.value)}
                  disabled={aiSpendBusy}
                  sx={{ width: 140 }}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <TextField
                  size="small"
                  label="Override max $"
                  value={overrideSpend}
                  onChange={(e) => setOverrideSpend(e.target.value)}
                  disabled={aiSpendBusy}
                  sx={{ width: 140 }}
                  inputProps={{ inputMode: 'decimal' }}
                />
                <TextField
                  size="small"
                  label="Override max rejections"
                  value={overrideRejectionMax}
                  onChange={(e) => setOverrideRejectionMax(e.target.value)}
                  disabled={rejectionBusy}
                  sx={{ width: 170 }}
                  inputProps={{ inputMode: 'numeric', min: 1, max: 20 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={aiSpendBusy}
                  onClick={() => void handleSaveAiSpendOverride()}
                  sx={{ textTransform: 'none' }}
                >
                  Save spend override
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={rejectionBusy}
                  onClick={() => void handleSaveRejectionCapOverride()}
                  sx={{ textTransform: 'none' }}
                >
                  Save rejection override
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={aiSpendBusy}
                  onClick={() => void handleResetAiSpend()}
                  sx={{ textTransform: 'none' }}
                >
                  Reset AI spend
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={rejectionBusy}
                  onClick={() => void handleResetRejectionCount()}
                  sx={{ textTransform: 'none' }}
                >
                  Reset rejection count
                </Button>
              </Stack>
              <Button
                size="small"
                color={editing.humanStopped === true ? 'success' : 'error'}
                variant="contained"
                disabled={humanStopBusy}
                startIcon={
                  humanStopBusy ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : null
                }
                onClick={handleHumanStopToggle}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                {humanStopBusy
                  ? 'Updating…'
                  : editing.humanStopped === true
                    ? 'Resume AI work'
                    : 'Stop AI work'}
              </Button>
            </>
          )}
        </Stack>
      )}
      <FormControl fullWidth size="small" disabled={ticketFormReadOnly}>
        <InputLabel>Priority</InputLabel>
        <Select label="Priority" value={formPriority} onChange={(e) => onFormPriorityChange(e.target.value)}>
          {PRIORITIES.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {(ticketApps.length > 0 || forceAppSelection) && (
        <FormControl
          fullWidth
          size="small"
          required={forceAppSelection}
          disabled={ticketFormReadOnly}
          error={forceAppSelection && !formAppId && !ticketFormReadOnly}
        >
          <InputLabel>App / Site</InputLabel>
          <Select
            label="App / Site"
            value={formAppId || ''}
            onChange={(e) => onFormAppIdChange?.(e.target.value)}
          >
            {!forceAppSelection && <MenuItem value="">None (Global Workspace Ticket)</MenuItem>}
            {ticketApps.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.appName}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {forceAppSelection && !formAppId && !ticketFormReadOnly
              ? 'Selecting an App/Site is required for new tickets.'
              : 'Scope this ticket to one of your Apps/Sites, or leave global.'}
          </FormHelperText>
        </FormControl>
      )}
      <FormControl fullWidth size="small" disabled={ticketFormReadOnly}>
        <InputLabel>Target Repository (Optional)</InputLabel>
        <Select
          label="Target Repository (Optional)"
          value={formTargetRepoKey || ''}
          onChange={(e) => {
            const nextPrimary = e.target.value;
            onFormTargetRepoKeyChange?.(nextPrimary);
            if (Array.isArray(formLinkedReadRepoKeys) && formLinkedReadRepoKeys.length) {
              onFormLinkedReadRepoKeysChange?.(
                formLinkedReadRepoKeys.filter((k) => k && k !== nextPrimary),
              );
            }
          }}
        >
          <MenuItem value="">
            Main Platform Repo
          </MenuItem>
          {gameProjectsWithGithub.length > 0 && (
            <ListSubheader disableSticky>Game Studio</ListSubheader>
          )}
          {gameProjectsWithGithub.map((project) => (
            <MenuItem key={`game:${project.id}`} value={`game:${project.id}`}>
              {project.title || 'Untitled game'} ({project.githubOwner}/
              {project.githubRepoName})
            </MenuItem>
          ))}
          {appProjectsWithGithub.length > 0 && (
            <ListSubheader disableSticky>AI Studio</ListSubheader>
          )}
          {appProjectsWithGithub.map((project) => (
            <MenuItem key={`app:${project.id}`} value={`app:${project.id}`}>
              {project.title || project.name || 'Untitled app'} (
              {project.githubOwner}/{project.githubRepoName})
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {formTargetRepoKey
            ? 'AI agents will submit Code Sandbox patches against this repository (human approval required before GitHub).'
            : 'Leave as Main Platform Repo for the default workspace GitHub target.'}
        </FormHelperText>
      </FormControl>
      <Box sx={{ mt: -0.5 }}>
        <Button
          size="small"
          color="inherit"
          disabled={ticketFormReadOnly || linkedReadRepoOptions.length === 0}
          onClick={() => setLinkedReposExpanded((v) => !v)}
          startIcon={linkedReposExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: 'none', px: 0.5 }}
        >
          Also read from…{selectedLinkedReadRepos.length
            ? ` (${selectedLinkedReadRepos.length})`
            : ''}
        </Button>
        <Collapse in={linkedReposExpanded}>
          <Autocomplete
            multiple
            size="small"
            options={linkedReadRepoOptions}
            groupBy={(o) => o.group}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.key === b.key}
            value={selectedLinkedReadRepos}
            onChange={(_, selected) => {
              const next = (selected || [])
                .map((o) => o.key)
                .filter((k) => k && k !== formTargetRepoKey)
                .slice(0, MAX_LINKED_READ_REPOS);
              onFormLinkedReadRepoKeysChange?.(next);
            }}
            disabled={ticketFormReadOnly}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.key}
                  size="small"
                  label={option.label}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Linked read-only repositories"
                placeholder={
                  selectedLinkedReadRepos.length >= MAX_LINKED_READ_REPOS
                    ? `Max ${MAX_LINKED_READ_REPOS}`
                    : 'Select up to 3'
                }
              />
            )}
            sx={{ mt: 1 }}
          />
          <FormHelperText sx={{ mx: 1.75 }}>
            Agents may read these repos via read_file / read_repo_tree (pass
            repo as owner/repoName). Patches still only target the primary
            repository above. Max {MAX_LINKED_READ_REPOS}.
            {editing && Array.isArray(editing.linkedReadRepos) && editing.linkedReadRepos.length
              ? ` Currently: ${editing.linkedReadRepos
                  .map((r) => formatTargetRepoSlug(r))
                  .filter(Boolean)
                  .join(', ')}.`
              : ''}
          </FormHelperText>
        </Collapse>
      </Box>
      <Autocomplete
        multiple
        options={labels}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={labels.filter((l) => formLabelIds.includes(l.id))}
        onChange={(_, v) => onFormLabelIdsChange(v.map((x) => x.id))}
        disabled={ticketFormReadOnly}
        renderInput={(params) => <TextField {...params} label="Labels" size="small" />}
      />
      <TicketAssigneeAutocomplete
        options={assigneeAutocompleteOptions}
        value={formAssignee}
        onChange={onFormAssigneeChange}
        disabled={ticketFormReadOnly}
        role={role}
      />
      {isCreate && isAdmin && (
        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary">
            Optional AI spend / rejection caps for this ticket (applied at create).
            Leave blank to use project/workspace/global defaults.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Override max calls"
              value={formAiSpendOverrideCalls}
              onChange={(e) => onFormAiSpendOverrideCalls?.(e.target.value)}
              disabled={ticketFormReadOnly}
              sx={{ width: 140 }}
              inputProps={{ inputMode: 'numeric' }}
            />
            <TextField
              size="small"
              label="Override max $"
              value={formAiSpendOverrideSpend}
              onChange={(e) => onFormAiSpendOverrideSpend?.(e.target.value)}
              disabled={ticketFormReadOnly}
              sx={{ width: 140 }}
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              size="small"
              label="Override max rejections"
              value={formRejectionCapOverride}
              onChange={(e) => onFormRejectionCapOverride?.(e.target.value)}
              disabled={ticketFormReadOnly}
              sx={{ width: 170 }}
              inputProps={{ inputMode: 'numeric', min: 1, max: 20 }}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );

  const footer = (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button onClick={onClose} disabled={saving}>
        {ticketFormReadOnly ? 'Close' : 'Cancel'}
      </Button>
      {!ticketFormReadOnly && (
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving || !formTitle.trim() || (forceAppSelection && !formAppId)}
        >
          {saving ? 'Saving…' : isCreate ? 'Create' : 'Save'}
        </Button>
      )}
    </Stack>
  );

  if (!fillHeight) {
    return (
      <>
        {body}
        <Box sx={{ mt: 2 }}>{footer}</Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, py: 1.5 }}>{body}</Box>
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {footer}
      </Box>
    </Box>
  );
}
